import React from "react";
import { get, includes, isEqual, isNil, pick, some } from "lodash";
import { Col, Form, Row } from "react-bootstrap";
import { Formik } from "formik";
import type { FormikProps } from "formik";

import DatePicker from "react-datepicker";
import moment from "moment";
import Button from "components/Button";
import InfoTooltip from "components/InfoTooltip";
import Alert from "components/Alert";
import { ScrollToFieldError } from "utils/form";
import {
  daysOfMonthOptions,
  END_OF_MONTH,
  parseDayString,
  formatDate,
  hasMinimumScheduleValues,
  PAYROLL_FREQUENCIES,
} from "utils/timeHelper";
import PayPeriodExamples from "./PayPeriodExamples";
import IconSpinner from "components/IconSpinner";

import * as yup from "yup";

const schema = yup.object({
  name: yup
    .string()
    .label("Group Name")
    .required()
    .min(2, "Must be at least two characters.")
    .max(100),
  associatedBankAccount: yup
    .string()
    .label("Associated Bank Account")
    .required(),
  useUniqueInviteCodes: yup.boolean(),
});

// A bank account option rendered in the associated-bank-account select.
interface BankAccount {
  id: string;
  bankName?: string | null;
  bankAlias?: string | null;
  accountId?: string | null;
}

// The group's pay schedule (from `props.group.schedule`).
interface GroupSchedule {
  payrollFrequency?: string | null;
  payrollProcessingOffset?: number | null;
  anchorPayDate?: string | null;
  dayOne?: number | string | null;
  dayTwo?: number | string | null;
  displayAnchorPayDate?: string | null;
  displayDayOne?: number | string | null;
  displayDayTwo?: number | string | null;
}

// The group passed in via props. The constructor spreads this whole object
// into state (and therefore into Formik's initialValues), so its fields flow
// through to the form values shape below.
interface Group {
  id: string;
  name: string;
  payrollFrequency?: string | null;
  useUniqueInviteCodes?: boolean | null;
  associatedBankAccount: { id: string };
  schedule: GroupSchedule;
  // Original code reads associatedSchedule.payPeriodEnd via lodash get.
  associatedSchedule?: { payPeriodEnd?: string | null } | null;
}

// The group object assembled in submitGroup / _addDisplayValues and handed to
// props.onSubmit.
interface GroupSubmitValues {
  id: unknown;
  name: string;
  associatedBankAccount: string;
  useUniqueInviteCodes?: boolean | null;
  displayDayOne?: number | null;
  displayDayTwo?: number | null;
  displayAnchorPayDate?: string | null;
}

interface EditGroupFormOwnProps {
  onClose?: () => void;
  onSubmit?: (group: GroupSubmitValues) => void;
  error?: string;
  accounts?: BankAccount[];
  isLoading?: boolean;
  group: Group;
  hasExpiredInvites?: boolean;
}

// State == Formik initialValues (constructor sets `this.state` and passes it as
// initialValues). It is `props.group` spread plus the extra form-control fields.
// `associatedBankAccount` is overwritten from the group's object form to the
// selected account id (a string), so it's omitted from Group and re-declared.
interface EditGroupFormState extends Omit<Group, "associatedBankAccount"> {
  associatedBankAccount: string;
  useUniqueInviteCodes?: boolean | null;
  useDisplayDates: boolean;
  displayAnchorPayDate?: string | null;
  displayDayOne?: number | string | null;
  displayDayTwo?: number | string | null;
  anchorPayDate?: string | null;
  isFetching: boolean;
  // Read in the display-date selects as a fallback for displayDayOne/Two.
  dayOne?: number | string | null;
  dayTwo?: number | string | null;
}

// Formik render-bag values use the same shape as state.
type EditGroupFormValues = EditGroupFormState;

export default class EditGroupForm extends React.PureComponent<
  EditGroupFormOwnProps,
  EditGroupFormState
> {
  constructor(props: EditGroupFormOwnProps) {
    super(props);

    const {
      schedule: {
        displayAnchorPayDate,
        displayDayOne,
        displayDayTwo,
        anchorPayDate,
      },
    } = props.group;

    const bankAccountId = props.group.associatedBankAccount.id;

    const groupHasActiveBankAccount = some(this.props.accounts, {
      id: bankAccountId,
    });

    const defaultAccount = !groupHasActiveBankAccount ? "" : bankAccountId;

    const useDisplayDates = Boolean(
      displayAnchorPayDate || displayDayOne || displayDayTwo
    );
    this.state = {
      ...props.group,
      associatedBankAccount: defaultAccount,
      useUniqueInviteCodes: props.group.useUniqueInviteCodes,
      useDisplayDates,
      displayAnchorPayDate,
      displayDayOne: displayDayOne === 31 ? END_OF_MONTH : displayDayOne,
      displayDayTwo: displayDayTwo === 31 ? END_OF_MONTH : displayDayTwo,
      anchorPayDate,
      isFetching: useDisplayDates,
    };
  }

  submitGroup = (values: EditGroupFormValues) => {
    let group: GroupSubmitValues = {
      id: this.state.id,
      name: values.name,
      associatedBankAccount: values.associatedBankAccount,
      useUniqueInviteCodes: values.useUniqueInviteCodes,
    };
    if (values.useDisplayDates) {
      group = this._addDisplayValues(
        group,
        values,
        this.props.group.payrollFrequency
      );
    }
    // onSubmit is optional in props but invoked unconditionally by the
    // original code; assert non-null to preserve that behavior.
    this.props.onSubmit!(group);
  };

  _addDisplayValues = (
    groupVals: GroupSubmitValues,
    values: EditGroupFormValues,
    payrollFrequency?: string | null
  ): GroupSubmitValues => {
    if (payrollFrequency === PAYROLL_FREQUENCIES.TWICE_PER_MONTH) {
      return {
        ...groupVals,
        displayDayOne: parseDayString(values.displayDayOne),
        displayDayTwo: parseDayString(values.displayDayTwo),
      };
    } else if (payrollFrequency === PAYROLL_FREQUENCIES.MONTHLY) {
      return {
        ...groupVals,
        displayDayOne: parseDayString(values.displayDayOne),
      };
    }
    return {
      ...groupVals,
      displayAnchorPayDate: formatDate(values.displayAnchorPayDate),
    };
  };

  _hasBeenUpdated = (values: EditGroupFormValues) => {
    const newGroup = {
      id: this.state.id,
      name: values.name,
      associatedBankAccount: values.associatedBankAccount,
      useUniqueInviteCodes: values.useUniqueInviteCodes,
      displayAnchorPayDate: values.displayAnchorPayDate,
      displayDayOne: parseDayString(values.displayDayOne),
      displayDayTwo: parseDayString(values.displayDayTwo),
    };
    const associatedBankAccount = get(
      this.props,
      "group.associatedBankAccount.id"
    );
    const {
      schedule: { displayAnchorPayDate, displayDayOne, displayDayTwo },
    } = this.props.group;
    const oldGroup = {
      ...pick(this.props.group, ["id", "name", "useUniqueInviteCodes"]),
      associatedBankAccount,
      displayAnchorPayDate,
      displayDayOne,
      displayDayTwo,
    };

    return (
      !isEqual(newGroup, oldGroup) ||
      this.state.useDisplayDates !== values.useDisplayDates
    );
  };

  _parseDate = (val?: moment.MomentInput) => {
    if (val) {
      return moment(val).toDate();
    }
    return val;
  };

  _showDayOne = (frequency?: string | null) => {
    return includes(
      [PAYROLL_FREQUENCIES.MONTHLY, PAYROLL_FREQUENCIES.TWICE_PER_MONTH],
      frequency
    );
  };

  _showDayTwo = (frequency?: string | null) => {
    return frequency === PAYROLL_FREQUENCIES.TWICE_PER_MONTH;
  };

  _getContent = ({
    handleChange,
    handleBlur,
    setFieldValue,
    values,
    errors,
    touched,
  }: Pick<
    FormikProps<EditGroupFormValues>,
    | "handleChange"
    | "handleBlur"
    | "setFieldValue"
    | "values"
    | "errors"
    | "touched"
  >) => {
    if (this.state.isFetching) {
      return (
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <IconSpinner />
        </div>
      );
    }

    // accounts is optional in props but the original code maps it directly;
    // assert non-null to preserve that runtime behavior.
    const accounts = this.props.accounts!.map((accounts, index) => {
      return (
        <option value={accounts.id} key={index}>
          {accounts.bankName} - {accounts.bankAlias} {accounts.accountId}
        </option>
      );
    });

    const endCurrentPayPeriod = get(
      this.props.group,
      "associatedSchedule.payPeriodEnd",
      null
    );
    return (
      <>
        <Row className="mb-3">
          <Form.Group as={Col} controlId="formGroupName">
            <Form.Label>Group Name</Form.Label>
            <Form.Control
              name="name"
              value={values.name}
              onChange={handleChange}
              isInvalid={!!errors.name}
              isValid={touched.name && !errors.name}
            />
            <Form.Control.Feedback type="invalid">
              {errors.name}
            </Form.Control.Feedback>
          </Form.Group>
        </Row>
        <Row className="mb-3">
          <Form.Group as={Col} controlId="formGroupBank">
            <Form.Label>Associated Bank Account</Form.Label>
            <Form.Select
              name="associatedBankAccount"
              value={values.associatedBankAccount}
              onChange={handleChange}
              isInvalid={!!errors.associatedBankAccount}
              isValid={
                touched.associatedBankAccount && !errors.associatedBankAccount
              }
            >
              <option value="" disabled>
                Associated Bank Account
              </option>
              {accounts}
            </Form.Select>
            <Form.Control.Feedback type="invalid">
              {errors.associatedBankAccount}
            </Form.Control.Feedback>
          </Form.Group>
        </Row>
        {this.props.hasExpiredInvites && (
          <Row className="mb-3">
            <Form.Group as={Col} sm={12} controlId="formBasicUniqueCode">
              <div className="d-flex align-items-center">
                <Form.Check
                  className="useUniqueInviteCodes"
                  type="checkbox"
                  name="useUniqueInviteCodes"
                  // @ts-expect-error preserved quirk: useUniqueInviteCodes may be
                  // null, but checked expects boolean | undefined
                  checked={values.useUniqueInviteCodes}
                  // @ts-expect-error preserved quirk: a boolean is passed to the
                  // value prop, which expects string | number | string[]
                  value={values.useUniqueInviteCodes}
                  onBlur={handleBlur}
                  label="Employee invites should expire"
                  onChange={(e) => {
                    void setFieldValue(
                      "useUniqueInviteCodes",
                      e.target.checked
                    );
                  }}
                />
                <InfoTooltip
                  tooltipBody="If checked, we will generate a unique invite code for each employee that will expire after 60 days."
                  placement="right"
                />
              </div>
            </Form.Group>
          </Row>
        )}
        <Row className="mb-3">
          <Form.Group as={Col} sm={12} controlId="useDisplayDates">
            <div className="d-flex align-items-center">
              <Form.Check
                className="useDisplayDates"
                type="checkbox"
                name="useDisplayDates"
                checked={values.useDisplayDates}
                // @ts-expect-error preserved quirk: a boolean is passed to the
                // value prop, which expects string | number | string[]
                value={values.useDisplayDates}
                onBlur={handleBlur}
                label="Pay period ends before payday"
                onChange={(e) => {
                  void setFieldValue("useDisplayDates", e.target.checked);
                }}
              />
              <InfoTooltip
                tooltipBody="If checked, you can configure the pay period end date."
                placement="right"
              />
            </div>
          </Form.Group>
        </Row>
        {values.useDisplayDates &&
          this._showDayOne(values.payrollFrequency) && (
            <Row className="mb-3">
              <Form.Group as={Col} md={6} controlId="formGroupDisplayDayOne">
                <Form.Label>
                  {values.payrollFrequency === "MONTHLY"
                    ? "What day of the month is the end of the pay period?"
                    : "What day of the month is the first end of the pay period?"}
                </Form.Label>
                <Form.Select
                  sm={6}
                  name="displayDayOne"
                  // @ts-expect-error preserved quirk: the fallback value may be
                  // null, but the value prop expects string | number | string[]
                  value={
                    isNil(values.displayDayOne)
                      ? values.dayOne
                      : values.displayDayOne
                  }
                  onChange={handleChange}
                  onBlur={handleBlur}
                  isInvalid={!!errors.displayDayOne}
                  isValid={touched.displayDayOne && !errors.displayDayOne}
                >
                  <option value="" disabled>
                    Day of Month
                  </option>
                  {daysOfMonthOptions.map((item) => (
                    <option value={item.value} key={item.key}>
                      {item.value}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
            </Row>
          )}
        {values.useDisplayDates &&
          this._showDayTwo(values.payrollFrequency) && (
            <Row className="mb-3">
              <Form.Group as={Col} md={6} controlId="formGroupDayTwo">
                <Form.Label>
                  What day of the month is the second end of the pay period?
                </Form.Label>
                <Form.Select
                  sm={6}
                  name="displayDayTwo"
                  // @ts-expect-error preserved quirk: the fallback value may be
                  // null, but the value prop expects string | number | string[]
                  value={
                    isNil(values.displayDayTwo)
                      ? values.dayTwo
                      : values.displayDayTwo
                  }
                  onChange={handleChange}
                  onBlur={handleBlur}
                  isInvalid={!!errors.displayDayTwo}
                  isValid={touched.displayDayTwo && !errors.displayDayTwo}
                >
                  <option value="" disabled>
                    Day of Month
                  </option>
                  {daysOfMonthOptions.map((item) => (
                    <option value={item.value} key={item.key}>
                      {item.value}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
            </Row>
          )}
        {values.useDisplayDates &&
          !this._showDayTwo(values.payrollFrequency) &&
          !this._showDayOne(values.payrollFrequency) && (
            <Row className="mb-3">
              <Form.Group as={Col} controlId="displayAnchorPayDate">
                <Form.Label>
                  What is the last day of your current pay period?
                </Form.Label>
                <div>
                  <DatePicker
                    className="form-control"
                    name="displayAnchorPayDate"
                    // @ts-expect-error preserved quirk: _parseDate may return a
                    // Date, but the value prop expects string | undefined
                    value={
                      isNil(values.displayAnchorPayDate)
                        ? this._parseDate(values.anchorPayDate)
                        : this._parseDate(values.displayAnchorPayDate)
                    }
                    dateFormat="MM/dd/yyyy"
                    // @ts-expect-error preserved quirk: _parseDate may return the
                    // original string value when given a non-date input
                    selected={this._parseDate(values.displayAnchorPayDate)}
                    onChange={(val: Date | null) => {
                      void setFieldValue(
                        "displayAnchorPayDate",
                        moment(val).toDate()
                      );
                    }}
                    isInvalid={!!errors.displayAnchorPayDate}
                    isValid={
                      touched.displayAnchorPayDate &&
                      !errors.displayAnchorPayDate
                    }
                    // @ts-expect-error preserved quirk: _parseDate may return the
                    // original string value when given a non-date input
                    maxDate={this._parseDate(endCurrentPayPeriod)}
                  />
                </div>
                {errors.displayAnchorPayDate &&
                  touched.displayAnchorPayDate && (
                    <div
                      className="invalid-feedback"
                      style={{ display: "block" }}
                    >
                      {errors.displayAnchorPayDate}
                    </div>
                  )}
              </Form.Group>
            </Row>
          )}
      </>
    );
  };

  render() {
    return (
      <Formik<EditGroupFormValues>
        validateOnChange={false}
        validationSchema={schema}
        onSubmit={(values) => {
          this.submitGroup(values);
        }}
        enableReinitialize={true}
        initialValues={this.state}
      >
        {({
          handleSubmit,
          setFieldValue,
          handleChange,
          handleBlur,
          values,
          touched,
          errors,
        }) => (
          <Form noValidate onSubmit={handleSubmit}>
            <ScrollToFieldError />
            {this._getContent({
              handleChange,
              handleBlur,
              setFieldValue,
              values,
              errors,
              touched,
            })}
            {hasMinimumScheduleValues({
              payrollFrequency: this.state.schedule.payrollFrequency,
              dayOne: parseDayString(this.state.schedule.dayOne),
              dayTwo: parseDayString(this.state.schedule.dayTwo),
              anchorPayDate: this.state.schedule.anchorPayDate,
            }) &&
              values.useDisplayDates && (
                <PayPeriodExamples
                  // PayPeriodExamples (already typed) expects non-null props,
                  // but these schedule fields and parse/format helpers may be
                  // null at runtime — cast at this boundary to the expected
                  // types without changing the values passed.
                  payrollFrequency={
                    this.state.schedule.payrollFrequency as string
                  }
                  dayOne={parseDayString(this.state.schedule.dayOne) as number}
                  dayTwo={parseDayString(this.state.schedule.dayTwo) as number}
                  anchorPayDate={this.state.schedule.anchorPayDate as string}
                  displayDayOne={parseDayString(values.displayDayOne) as number}
                  displayDayTwo={parseDayString(values.displayDayTwo) as number}
                  displayAnchorPayDate={
                    formatDate(values.displayAnchorPayDate) as string
                  }
                  payrollProcessingOffset={
                    this.state.schedule.payrollProcessingOffset as number
                  }
                  onComplete={() => {
                    this.setState({ isFetching: false });
                  }}
                />
              )}
            {this.props.error && <Alert type="error" msg={this.props.error} />}
            <div className="submit-row">
              <span className="cancel-btn">
                <Button
                  btnLabel="Cancel"
                  color="cancel"
                  name="cancel"
                  type="button"
                  onClick={this.props.onClose}
                />
              </span>
              <Button
                name="submit"
                btnLabel="Update Group"
                withArrow={true}
                loading={this.props.isLoading}
              />
            </div>
          </Form>
        )}
      </Formik>
    );
  }
}
